import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { NAV_ITEMS } from "@/constants/navItems";
import { ROLE_ACCENT, usePermissions } from "@/lib/permissions";
import { drawerRight, modalOverlay, useSafeMotion } from "@/components/altiora/motion";
import { NAVY, NAVY_DARK, CHAMPAGNE, GOLD, IVORY, SLATE } from "@/components/altiora/PremiumUI";

/**
 * MobileNav — hamburger trigger + slide-in drawer for < md screens.
 *
 * The dedicated `<Sidebar>` component is `hidden md:flex`, which used to
 * leave mobile users without navigation. This component fills that gap
 * with a Notion-style right-anchored drawer, ARIA-compliant, and closes
 * on route change / backdrop click / ESC.
 */
export function MobileNav() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { permissions, roles, impersonatingAs } = usePermissions();
  const location = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const overlay = useSafeMotion(modalOverlay);
  const panel   = useSafeMotion(drawerRight);

  // Close on route change (post-navigation)
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // ESC support
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  // RBAC filter (same rules as Sidebar/CommandPalette)
  const permsSet = new Set(permissions);
  const rolesSet = new Set(roles);
  const items = NAV_ITEMS.filter((i) => {
    if (!i.perm) return true;
    if (permsSet.has(i.perm)) return true;
    if (!impersonatingAs && (rolesSet.has("founder") || rolesSet.has("super_admin"))) return true;
    return false;
  });

  const primary  = items.filter((i) => i.group === "primary");
  const personal = items.filter((i) => i.group === "personal");
  const admin    = items.filter((i) => i.group === "admin");

  return (
    <>
      {/* Hamburger trigger — visible only on < md */}
      <button
        onClick={() => setOpen(true)}
        data-testid="mobile-nav-trigger"
        aria-label="Ouvrir le menu"
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
        className="md:hidden inline-flex items-center justify-center rounded-md h-8 w-8 transition
                        hover:opacity-90"
        style={{ border: `1px solid ${CHAMPAGNE}44`, color: `${IVORY}CC` }}>
        <Menu size={16} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div key="mn-ov" {...overlay}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[65] md:hidden"
              style={{ background: "rgba(6,21,40,0.55)", backdropFilter: "blur(4px)" }}
              data-testid="mobile-nav-overlay" />
            <motion.aside key="mn-p" {...panel}
              id="mobile-nav-drawer"
              role="dialog" aria-modal="true" aria-label="Navigation principale"
              data-testid="mobile-nav-drawer"
              className="fixed top-0 right-0 h-full w-[86%] max-w-[320px] z-[66]
                              flex flex-col overflow-hidden md:hidden"
              style={{ background: NAVY_DARK, color: IVORY,
                          borderLeft: `1px solid ${CHAMPAGNE}22` }}>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b"
                      style={{ borderColor: `${CHAMPAGNE}1F` }}>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
                          style={{ color: CHAMPAGNE }}>
                    Navigation
                  </div>
                  {user?.primary_role && (
                    <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-mono
                                          uppercase tracking-widest px-2 py-0.5 border rounded-sm"
                            style={{ borderColor: (ROLE_ACCENT[user.primary_role] || ROLE_ACCENT.member).color,
                                        color: (ROLE_ACCENT[user.primary_role] || ROLE_ACCENT.member).color }}>
                      {(ROLE_ACCENT[user.primary_role] || ROLE_ACCENT.member).label}
                    </div>
                  )}
                </div>
                <button onClick={() => setOpen(false)} aria-label="Fermer le menu"
                            data-testid="mobile-nav-close"
                            className="p-1.5 rounded-md hover:opacity-80 transition"
                            style={{ border: `1px solid ${CHAMPAGNE}33`, color: IVORY }}>
                  <X size={16} aria-hidden="true" />
                </button>
              </div>

              {/* Scrollable nav */}
              <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
                {primary.map((it) => (
                  <MobileNavItem key={it.key} item={it} pathname={location.pathname} t={t} />
                ))}
                {personal.length > 0 && (
                  <SectionLabel>Mon trajet</SectionLabel>
                )}
                {personal.map((it) => (
                  <MobileNavItem key={it.key} item={it} pathname={location.pathname} t={t} />
                ))}
                {admin.length > 0 && (
                  <SectionLabel>Administration</SectionLabel>
                )}
                {admin.map((it) => (
                  <MobileNavItem key={it.key} item={it} pathname={location.pathname} t={t} />
                ))}
              </nav>

              {/* Footer */}
              <div className="border-t px-3 py-3 flex items-center gap-2"
                      style={{ borderColor: `${CHAMPAGNE}22` }}>
                <div className="flex-1 min-w-0 text-[11px] truncate"
                        style={{ color: `${IVORY}88` }}>
                  {user?.email}
                </div>
                <button
                  onClick={async () => { await logout(); nav("/"); setOpen(false); }}
                  data-testid="mobile-nav-logout"
                  aria-label={t("nav.logout")}
                  className="p-2 rounded-md transition hover:opacity-80"
                  style={{ border: `1px solid ${CHAMPAGNE}33`, color: IVORY }}>
                  <LogOut size={14} aria-hidden="true" />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-4 pt-4 pb-1 text-[9px] font-semibold uppercase tracking-[0.28em]"
            style={{ color: CHAMPAGNE }}>
      {children}
    </div>
  );
}

function MobileNavItem({ item, pathname, t }) {
  const Icon = item.icon;
  const active = pathname === item.to;
  return (
    <Link
      to={item.to}
      data-testid={`mobile-${item.testId}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition"
      style={{
        background: active ? `${CHAMPAGNE}22` : "transparent",
        color:      active ? GOLD          : IVORY,
        fontWeight: active ? 600           : 400,
      }}>
      <Icon size={16} aria-hidden="true"
                strokeWidth={active ? 2.2 : 1.7}
                style={{ color: active ? CHAMPAGNE : `${IVORY}CC` }} />
      <span className="truncate">{t(item.label)}</span>
    </Link>
  );
}
